/**
 * prelim/generator.tsx
 *
 * Prelim Generator page — search for a title, configure overrides,
 * generate all 4 prelim pages, preview XML in tabs, download files.
 */

import { useState, useEffect, useCallback } from "react";
import { useMutation } from "@tanstack/react-query";
import { useQuery } from "@tanstack/react-query";
import { PageWrapper } from "@/components/layout/PageWrapper";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  searchPrelimTitles,
  generatePrelimPages,
  downloadPrelimPage,
  downloadAllPrelimPages,
  DEFAULT_OVERRIDES,
  type PrelimOverrides,
  type PrelimPageOutput,
} from "@/lib/prelim-api";
import type { CopyrightSearchResult } from "@/lib/copyright-api";

const IMPRINT_OPTIONS = [
  { value: '', label: 'From data (default)' },
  { value: 'PH04', label: 'New York (PH04)' },
  { value: 'PH02', label: 'Berlin (PH02)' },
  { value: 'PH05', label: 'Brussels (PH05)' },
  { value: 'PH03', label: 'Oxford (PH03)' },
  { value: 'PH01', label: 'Lausanne (PH01)' },
];

const LANGUAGE_OPTIONS = [
  { value: '', label: 'From data (default)' },
  { value: 'ENGL', label: 'English' },
  { value: 'GER', label: 'German' },
  { value: 'FRE', label: 'French' },
  { value: 'SPA', label: 'Spanish' },
  { value: 'POR', label: 'Portuguese' },
  { value: 'ITA', label: 'Italian' },
  { value: 'GLG', label: 'Galician' },
];

export default function PrelimGeneratorPage() {
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [selectedTitle, setSelectedTitle] = useState<CopyrightSearchResult | null>(null);
  const [overrides, setOverrides] = useState<PrelimOverrides>({ ...DEFAULT_OVERRIDES });
  const [pages, setPages] = useState<PrelimPageOutput[] | null>(null);
  const [activeTab, setActiveTab] = useState<string>('half-title');

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchTerm.trim().length >= 2) {
        setDebouncedSearch(searchTerm.trim());
      } else {
        setDebouncedSearch("");
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Search query
  const { data: searchResults, isLoading: searchLoading, error: searchError } = useQuery({
    queryKey: ["prelim-search", debouncedSearch],
    queryFn: () => searchPrelimTitles(debouncedSearch),
    enabled: debouncedSearch.length >= 2 && !selectedTitle,
  });

  // Generate mutation
  const generateMutation = useMutation({
    mutationFn: () => generatePrelimPages(selectedTitle!.titleId, overrides),
    onSuccess: (data) => {
      setPages(data);
      if (data.length > 0) {
        setActiveTab(data[0].type);
      }
    },
  });

  const handleBack = useCallback(() => {
    setSelectedTitle(null);
    setPages(null);
    setOverrides({ ...DEFAULT_OVERRIDES });
  }, []);

  const handleGenerate = useCallback(() => {
    generateMutation.mutate();
  }, [generateMutation]);

  const handleSelectTitle = useCallback((result: CopyrightSearchResult) => {
    setSelectedTitle(result);
    setPages(null);
    setOverrides({ ...DEFAULT_OVERRIDES });
  }, []);

  const updateOverride = useCallback(<K extends keyof PrelimOverrides>(key: K, value: PrelimOverrides[K]) => {
    setOverrides(prev => ({ ...prev, [key]: value }));
  }, []);

  return (
    <PageWrapper>
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900">Prelim Generator</h1>
          <p className="text-sm text-gray-500 mt-1">
            Generate all 4 prelim pages (half-title, series, title, copyright) as BITS XML.
          </p>
        </div>

        {selectedTitle ? (
          /* ============ DETAIL VIEW ============ */
          <div>
            {/* Back button */}
            <Button variant="ghost" size="sm" className="mb-4" onClick={handleBack}>
              <span className="material-icons text-sm mr-1">arrow_back</span>
              Back to search
            </Button>

            {/* Title summary */}
            <Card className="mb-4">
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div>
                    <CardTitle className="text-lg">{selectedTitle.title}</CardTitle>
                    {selectedTitle.subtitle && (
                      <p className="text-sm text-gray-500 mt-1">{selectedTitle.subtitle}</p>
                    )}
                  </div>
                  <Badge variant="secondary">{selectedTitle.publishingHouse}</Badge>
                </div>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-4 text-sm text-gray-600">
                  {selectedTitle.authorNames && (
                    <div><span className="font-medium">Author:</span> {selectedTitle.authorNames}</div>
                  )}
                  {selectedTitle.editorNames && (
                    <div><span className="font-medium">Editor:</span> {selectedTitle.editorNames}</div>
                  )}
                  {selectedTitle.copyrightYear && (
                    <div><span className="font-medium">Year:</span> {selectedTitle.copyrightYear}</div>
                  )}
                  {selectedTitle.isbnPrint && (
                    <div><span className="font-medium">ISBN:</span> {selectedTitle.isbnPrint}</div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Overrides Form */}
            <Card className="mb-4">
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Options</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Imprint override */}
                  <div className="space-y-1.5">
                    <Label htmlFor="imprint">Imprint</Label>
                    <Select
                      value={overrides.imprintOverride || ''}
                      onValueChange={(v) => updateOverride('imprintOverride', v || null)}
                    >
                      <SelectTrigger id="imprint">
                        <SelectValue placeholder="From data (default)" />
                      </SelectTrigger>
                      <SelectContent>
                        {IMPRINT_OPTIONS.map(opt => (
                          <SelectItem key={opt.value} value={opt.value || '_default'}>
                            {opt.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Language override */}
                  <div className="space-y-1.5">
                    <Label htmlFor="language">Language</Label>
                    <Select
                      value={overrides.languageOverride || ''}
                      onValueChange={(v) => updateOverride('languageOverride', v || null)}
                    >
                      <SelectTrigger id="language">
                        <SelectValue placeholder="From data (default)" />
                      </SelectTrigger>
                      <SelectContent>
                        {LANGUAGE_OPTIONS.map(opt => (
                          <SelectItem key={opt.value} value={opt.value || '_default'}>
                            {opt.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Toggle switches */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                  <div className="flex items-center space-x-2">
                    <Switch
                      id="pl-classics"
                      checked={overrides.isPLClassics}
                      onCheckedChange={(v) => updateOverride('isPLClassics', v)}
                    />
                    <Label htmlFor="pl-classics">PL Classics</Label>
                  </div>

                  <div className="flex items-center space-x-2">
                    <Switch
                      id="dissertation"
                      checked={overrides.isDissertation}
                      onCheckedChange={(v) => updateOverride('isDissertation', v)}
                    />
                    <Label htmlFor="dissertation">Dissertation</Label>
                  </div>

                  <div className="flex items-center space-x-2">
                    <Switch
                      id="funding"
                      checked={overrides.hasFunding}
                      onCheckedChange={(v) => updateOverride('hasFunding', v)}
                    />
                    <Label htmlFor="funding">Funding</Label>
                  </div>

                  <div className="flex items-center space-x-2">
                    <Switch
                      id="cover-image"
                      checked={overrides.hasCoverImageCredit}
                      onCheckedChange={(v) => updateOverride('hasCoverImageCredit', v)}
                    />
                    <Label htmlFor="cover-image">Cover Image Credit</Label>
                  </div>

                  <div className="flex items-center space-x-2">
                    <Switch
                      id="external-designer"
                      checked={overrides.hasExternalDesigner}
                      onCheckedChange={(v) => updateOverride('hasExternalDesigner', v)}
                    />
                    <Label htmlFor="external-designer">External Designer</Label>
                  </div>
                </div>

                {/* Conditional text fields */}
                <div className="space-y-3 mt-4">
                  {overrides.isDissertation && (
                    <div className="space-y-1.5">
                      <Label htmlFor="dissertation-text">Dissertation Statement</Label>
                      <Input
                        id="dissertation-text"
                        placeholder='e.g., Zugl.: Wien, Univ., Diss., 2023'
                        value={overrides.dissertationStatement || ''}
                        onChange={(e) => updateOverride('dissertationStatement', e.target.value || null)}
                      />
                    </div>
                  )}

                  {overrides.hasFunding && (
                    <div className="space-y-1.5">
                      <Label htmlFor="funding-text">Funding Statement</Label>
                      <Input
                        id="funding-text"
                        placeholder="e.g., Deutsche Forschungsgemeinschaft"
                        value={overrides.fundingStatement || ''}
                        onChange={(e) => updateOverride('fundingStatement', e.target.value || null)}
                      />
                    </div>
                  )}

                  {overrides.hasCoverImageCredit && (
                    <div className="space-y-1.5">
                      <Label htmlFor="cover-credit">Cover Image Credit</Label>
                      <Input
                        id="cover-credit"
                        placeholder="e.g., Photo by John Doe / Unsplash"
                        value={overrides.coverImageCredit || ''}
                        onChange={(e) => updateOverride('coverImageCredit', e.target.value || null)}
                      />
                    </div>
                  )}

                  {overrides.hasExternalDesigner && (
                    <div className="space-y-1.5">
                      <Label htmlFor="designer-name">Designer Name</Label>
                      <Input
                        id="designer-name"
                        placeholder="e.g., Jane Designer"
                        value={overrides.coverDesignerName || ''}
                        onChange={(e) => updateOverride('coverDesignerName', e.target.value || null)}
                      />
                    </div>
                  )}

                  {/* Depot legal — always visible for user convenience */}
                  <div className="space-y-1.5">
                    <Label htmlFor="depot-legal">Depot Legal Number (Belgium only)</Label>
                    <Input
                      id="depot-legal"
                      placeholder="e.g., D/2025/5678/001"
                      value={overrides.depotLegalNumber || ''}
                      onChange={(e) => updateOverride('depotLegalNumber', e.target.value || null)}
                    />
                  </div>
                </div>

                {/* Generate button */}
                <div className="mt-6">
                  <Button
                    onClick={handleGenerate}
                    disabled={generateMutation.isPending}
                    className="w-full md:w-auto"
                  >
                    {generateMutation.isPending ? (
                      <>
                        <span className="material-icons text-sm mr-1 animate-spin">refresh</span>
                        Generating...
                      </>
                    ) : (
                      <>
                        <span className="material-icons text-sm mr-1">auto_awesome</span>
                        Generate Prelim Pages
                      </>
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Error */}
            {generateMutation.isError && (
              <div className="text-red-600 bg-red-50 border border-red-200 rounded-md p-4 text-sm mb-4">
                <span className="font-medium">Generation failed:</span>{" "}
                {(generateMutation.error as Error).message}
              </div>
            )}

            {/* Preview tabs */}
            {pages && pages.length > 0 && (
              <Card>
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-base">Generated Pages</CardTitle>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => downloadAllPrelimPages(pages, selectedTitle.titleId, selectedTitle.title)}
                    >
                      <span className="material-icons text-sm mr-1">download</span>
                      Download All
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  <Tabs value={activeTab} onValueChange={setActiveTab}>
                    <TabsList className="mb-4">
                      {pages.map((page) => (
                        <TabsTrigger key={page.type} value={page.type}>
                          {page.label}
                        </TabsTrigger>
                      ))}
                    </TabsList>

                    {pages.map((page) => (
                      <TabsContent key={page.type} value={page.type}>
                        <div className="flex justify-end mb-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => downloadPrelimPage(page, selectedTitle.titleId, selectedTitle.title)}
                          >
                            <span className="material-icons text-sm mr-1">download</span>
                            Download {page.label}
                          </Button>
                        </div>
                        <pre className="bg-gray-50 border rounded-md p-4 text-xs font-mono overflow-x-auto max-h-[600px] overflow-y-auto whitespace-pre-wrap">
                          {page.xml}
                        </pre>
                      </TabsContent>
                    ))}
                  </Tabs>
                </CardContent>
              </Card>
            )}
          </div>
        ) : (
          /* ============ SEARCH VIEW ============ */
          <div>
            {/* Search input */}
            <div className="relative mb-6">
              <span className="material-icons absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-lg">
                search
              </span>
              <Input
                placeholder="Search by title, ISBN, or Title ID..."
                className="h-12 pl-10"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                autoFocus
              />
            </div>

            {/* Search results */}
            {searchLoading && (
              <div className="space-y-3">
                {[1, 2, 3].map((i) => (
                  <Card key={i}>
                    <CardContent className="p-4">
                      <Skeleton className="h-5 w-1/2 mb-2" />
                      <Skeleton className="h-4 w-1/3" />
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}

            {!searchLoading && searchResults && searchResults.length > 0 && (
              <div className="space-y-3">
                {searchResults.map((result) => (
                  <Card
                    key={result.titleId}
                    className="cursor-pointer hover:border-blue-300 hover:shadow-sm transition-all"
                    onClick={() => handleSelectTitle(result)}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex-1 min-w-0">
                          <h3 className="font-medium text-gray-900 truncate">
                            {result.title}
                          </h3>
                          {result.subtitle && (
                            <p className="text-sm text-gray-500 truncate">
                              {result.subtitle}
                            </p>
                          )}
                          <div className="flex flex-wrap gap-2 mt-2 text-xs text-gray-500">
                            {result.authorNames && <span>{result.authorNames}</span>}
                            {result.editorNames && <span>Ed: {result.editorNames}</span>}
                          </div>
                        </div>
                        <div className="flex flex-col items-end gap-1 ml-4 flex-shrink-0">
                          {result.publishingHouse && (
                            <Badge variant="secondary" className="text-xs">
                              {result.publishingHouse}
                            </Badge>
                          )}
                          {result.copyrightYear && (
                            <span className="text-xs text-gray-400">{result.copyrightYear}</span>
                          )}
                          {result.isbnPrint && (
                            <span className="text-xs text-gray-400 font-mono">{result.isbnPrint}</span>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}

            {!searchLoading && searchError && (
              <div className="text-red-600 bg-red-50 border border-red-200 rounded-md p-4 text-sm">
                <span className="font-medium">Search failed:</span>{" "}
                {(searchError as Error).message}
              </div>
            )}

            {!searchLoading && !searchError && debouncedSearch && searchResults?.length === 0 && (
              <div className="text-center py-12 text-gray-400">
                <span className="material-icons text-4xl mb-2 block">search_off</span>
                <p>No titles found for "{debouncedSearch}"</p>
              </div>
            )}

            {!debouncedSearch && !searchLoading && (
              <div className="text-center py-12 text-gray-400">
                <span className="material-icons text-4xl mb-2 block">auto_stories</span>
                <p>Enter a title name, ISBN, or Title ID to search</p>
              </div>
            )}
          </div>
        )}
      </div>
    </PageWrapper>
  );
}
